import React, { useState } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import { MessageBundle } from '@amzn/arb-tools';
import { house, houseSolid } from '@amzn/storm-ui-icons';
import { Icon, Tooltip, useIsMobile } from '@amzn/storm-ui';
import { useSidenavBundle } from 'src/providers/PantherLocalizationProvider';
import { overrideMouseLeaveTimeout, StylelessButton } from 'src/commons/styles';
import {
  setLandingAppAction,
  unsetLandingAppAction,
} from 'src/commons/utils/setLandingApp';
import { BANNER_TYPE } from 'src/app/slice';
import {
  SnackbarContainer,
  ToastBanner,
} from 'src/features/header/components/ToastBanner/ToastBanner';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';

export const enum LANDING_PLACEMENT {
  NAVIGATION,
  HEADER,
  CONSOLIDATED_NAV,
}

export const LandingAppWrapper = styled.span<{
  placement: LANDING_PLACEMENT;
  isLandingApp: boolean;
}>`
  display: inline;
  align-self: center;
  font-size: ${({ placement }) =>
    placement === LANDING_PLACEMENT.HEADER ? '16px' : '14px'};
  margin-inline-start: ${({ placement, theme }) =>
    placement === LANDING_PLACEMENT.HEADER
      ? theme.spacing.xxsmall.default
      : null};
  color: ${({ theme, isLandingApp, placement }) =>
    placement === LANDING_PLACEMENT.HEADER
      ? isLandingApp
        ? theme.color.gray[0]
        : theme.color.gray[600]
      : theme.color.gray[900]};

  &:hover {
    color: ${({ theme, placement }) =>
      placement === LANDING_PLACEMENT.HEADER
        ? theme.color.gray[0]
        : theme.color.gray[900]};
  }

  &&&& {
    button {
      ${({ placement }) =>
        placement === LANDING_PLACEMENT.NAVIGATION &&
        css`
          padding: ${({ theme }) => theme.spacing.xsmall.default} 12px !important;
        `}
      ${({ placement }) =>
        placement === LANDING_PLACEMENT.CONSOLIDATED_NAV &&
        css`
          width: 40px;
          height: 40px;
          border-radius: 5px;
        `}
    }
  }

  ${({ placement }) => placement === LANDING_PLACEMENT.NAVIGATION && css``}
`;

const HouseIcon = styled(Icon)`
  pointer-events: none;
`;

interface LandingIconProps {
  appName: string;
  landingAppName: string;
  tooltipLabel: string;
  landingPlacement: LANDING_PLACEMENT;
  tabIndex?: number;
  updateLandingAppName: (...args: any[]) => any;
}

interface ToastBannerProps {
  showToastBanner: boolean;
  toastBannerType?: BANNER_TYPE;
}

const nonLandingIconId = 'non-landing-app';

const getToastBannerMessage = (
  bundle: MessageBundle,
  isLandingApp: boolean,
  bannerType: BANNER_TYPE,
): string => {
  if (bannerType === BANNER_TYPE.ERROR) {
    if (isLandingApp) {
      return bundle.getMessage('sidenav-landing-page-unset-toast-banner-error');
    }
    return bundle.getMessage('sidenav-landing-page-set-toast-banner-error');
  }

  if (isLandingApp) {
    return bundle.getMessage('sidenav-landing-page-set-toast-banner-success');
  }
  return bundle.getMessage('sidenav-landing-page-unset-toast-banner-success');
};

export const LandingAppIcon: React.VFC<LandingIconProps> = ({
  appName,
  landingAppName,
  tooltipLabel,
  landingPlacement,
  tabIndex,
  updateLandingAppName,
}) => {
  const bundle = useSidenavBundle();
  const isMobile = useIsMobile();
  const [toastBanner, setToastBanner] = useState<ToastBannerProps>({
    showToastBanner: false,
    toastBannerType: undefined,
  });
  const [enableClick, setEnableClick] = useState(true);
  const isLandingApp = appName != '' && landingAppName === appName;
  const taktPlacement =
    landingPlacement === LANDING_PLACEMENT.HEADER ? 'header' : 'nav';
  //TAKT delay emit onClick event, when TAKT emits onClick, landingApp has been set
  const taktOperation = !isLandingApp ? 'set' : 'unset';
  const landingAppId = `aac-${taktOperation}-landing-${taktPlacement}-${appName}`;

  const tooltipMessage = (isArialLabel: boolean) =>
    bundle.formatMessage(
      isLandingApp
        ? 'sidenav-landing-page-unset-label'
        : 'sidenav-landing-page-set-label',
      { application: isArialLabel ? tooltipLabel : '' },
    );

  const onClickEvent = async (e): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    if (!enableClick) return;
    setEnableClick(false);
    isLandingApp
      ? unsetLandingAppAction({
          updateLandingAppName,
          setToastBanner,
        })
      : setLandingAppAction({
          appName: appName,
          updateLandingAppName,
          setToastBanner,
        });
    e.target.blur();
    setEnableClick(true);
  };

  const landingIcon = (
    <StylelessButton
      id={landingAppId}
      variant="transparent"
      onClick={onClickEvent}
      tabIndex={tabIndex}
      role="switch"
      aria-checked={isLandingApp ? 'true' : 'false'}
      aria-label={tooltipMessage(true)}
      isRenderedInHeader={landingPlacement === LANDING_PLACEMENT.HEADER}
      {...getTaktAttributes(
        landingAppId,
        landingPlacement === LANDING_PLACEMENT.HEADER
          ? chromeComponents.HEADER
          : chromeComponents.NAV,
      )}
      data-ccx-e2e-id={landingAppId}
    >
      <HouseIcon
        type={isLandingApp ? houseSolid : house}
        id={
          !isLandingApp &&
          [
            LANDING_PLACEMENT.NAVIGATION,
            LANDING_PLACEMENT.CONSOLIDATED_NAV,
          ].includes(landingPlacement)
            ? nonLandingIconId + '-' + appName
            : appName
        }
      />
    </StylelessButton>
  );

  const desktopLandingIcon = (
    <ThemeProvider theme={overrideMouseLeaveTimeout}>
      <Tooltip
        trigger={landingIcon}
        message={tooltipMessage(false)}
        nonInteractive
      />
    </ThemeProvider>
  );

  return (
    <>
      <LandingAppWrapper
        placement={landingPlacement}
        isLandingApp={isLandingApp}
      >
        {isMobile ? landingIcon : desktopLandingIcon}
      </LandingAppWrapper>
      {toastBanner.showToastBanner && toastBanner.toastBannerType && (
        <SnackbarContainer>
          <ToastBanner
            banner={{
              id: 'landing-experience-toast-banner',
              message: getToastBannerMessage(
                bundle,
                isLandingApp,
                toastBanner.toastBannerType,
              ),
              type: toastBanner.toastBannerType,
              withCloseButton: false,
            }}
            bannerClosed={() => {
              setToastBanner({
                showToastBanner: false,
                toastBannerType: undefined,
              });
            }}
            autoClose={true}
          />
        </SnackbarContainer>
      )}
    </>
  );
};
